import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Routes, Route, useLocation, useSearchParams } from "react-router";
import Layout from "./Layout";
import Loading from "@components/Statuses/Loading";
import Fail from "@components/Statuses/Fail";
import Button from "@components/Button";

const NotFound = lazy(() => import("@pages/NotFound"));
const LoginPage = lazy(() => import("@pages/LoginPage"));
const QrEntryPage = lazy(() => import("@pages/QrEntryPage"));
const CouponEntryPage = lazy(() => import("@pages/CouponEntryPage"));
const MigratePage = lazy(() => import("@pages/MigratePage"));
const VerifyPage = lazy(() => import("@pages/VerifyPage"));
const SchufaPage = lazy(() => import("@pages/VerifyPage/SchufaPage"));
const PhoneNumberBypass = lazy(() => import("@pages/VerifyPage/PhoneNumber"));
const ResetPage = lazy(() => import("@pages/ResetPage"));
const SetPasswordPage = lazy(() => import("@pages/SetPasswordPage"));
const RegisterPage = lazy(() => import("@pages/RegisterPage"));
const RegisterSetPasswordPage = lazy(() => import("@pages/RegisterSetPasswordPage"));
const JoinPage = lazy(() => import("@pages/JoinPage"));
const ConfirmPage = lazy(() => import("@pages/ConfirmPage"));
const ImpressumPage = lazy(() => import("@pages/Impressum"));
const DatenschutzPage = lazy(() => import("@pages/Datenschutz"));
const NutzungsbedingungenPage = lazy(() => import("@pages/NutzungsbedingungenPage"));
const TeilnahmebedingungenTreueprogrammPage = lazy(() => import("@pages/TeilnahmebedingungenTreueprogramm"));

function Router() {
    const previousUrl = useRef(window.referrer || "")
    const [searchParams] = useSearchParams()
    const {pathname} = useLocation()

    useEffect(() => {
      let params = false;

      for(let entry of searchParams.entries()) {
        if(!params) params = {}
        if(entry[0]?.includes("utm_")) params[entry[0]] = entry[1]
      }

      if(params) sessionStorage.setItem("utmParams", JSON.stringify(params))
    }, [])

    class ErrorBoundary extends React.Component {
        constructor(props) {
          super(props);
          this.state = { hasError: false, error: "", errorInfo: "" };
        }
      
        static getDerivedStateFromError(error) {
          return { hasError: true };
        }
      
        componentDidCatch(error, errorInfo) {
          this.setState({ error: error, errorInfo: errorInfo })
        }
      
        render() {
          if (this.state.hasError) {
            return <div className="error-boundary">
                <Fail>
                  <h2>Something went wrong.</h2>
                  <p dangerouslySetInnerHTML={{ __html: (this.state.error) }}></p>
                  {/* <p dangerouslySetInnerHTML={{ __html: (this.state.errorInfo?.componentStack)?.replaceAll("\n", "<br/>") }}></p> */}
                  <p><strong>Path:</strong> {document.location.href}</p>
                  <p><strong>UserAgent:</strong> {navigator.userAgent}</p>
                  <p><strong>App version ID:</strong> {import.meta.env.VITE_COMMIT_ID}</p>
                  { document.querySelector("html.translated-ltr, html.translated-rtl, ya-tr-span, *[_msttexthash], *[x-bergamot-translated]") ? <p><strong translate="no">Please try again without a translator!</strong></p> : null }
                  <Button action={() => { location.reload() }}>Seite neu laden</Button>
                </Fail>
            </div>
          }
      
          return this.props.children; 
        }
    }

    useLayoutEffect(() => {
      document.documentElement.scrollTo(0, 0);
      if(window.dataLayer) {
        const UTMs = sessionStorage.getItem("utmParams") ? JSON.parse(sessionStorage.getItem("utmParams")) : false
        setTimeout(() => {
          window.dataLayer.push({
            event: "page_view",
            page_name: pathname,
            page_referrer: previousUrl.current,
            // page_name: document.title,
            user_login_source: sessionStorage.getItem("ls") || UTMs?.utm_campaign || "direct"
          })
          previousUrl.current = pathname
        }, 1000)
      }
    }, [pathname]);

    return (
        <ErrorBoundary>
          <Suspense fallback={<Loading fullScreen>
            <p>Gib uns einen Augenblick</p>
          </Loading>}>
            <Routes>
                <Route element={<Layout/>}>
                    <Route path="/" element={<LoginPage />} />
                    <Route path="qr" element={<QrEntryPage />} />
                    <Route path="coupon/:campaign/:ident" element={<CouponEntryPage />} />
                    <Route path="/migrate" element={<MigratePage />} />
                    <Route path="register" element={<RegisterPage />} />
                    <Route path="register/set-password" element={<RegisterSetPasswordPage />} />
                    <Route path="c/:campaign/:referral?" element={<RegisterPage />} />
                    <Route path="join" element={<JoinPage />} />
                    <Route path="confirm" element={<ConfirmPage />} />
                    <Route path="reset-password" element={<ResetPage />} />
                    <Route path="set-password" element={<SetPasswordPage />} />
                    <Route path="legal/impressum" element={<ImpressumPage />} />
                    <Route path="legal/datenschutz" element={<DatenschutzPage />} />
                    <Route path="legal/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
                    <Route path="teilnahmebedingungen-treueprogramm" element={<TeilnahmebedingungenTreueprogrammPage />} />
                    <Route path="verify" element={<VerifyPage />} >
                      <Route path="schufa" element={<SchufaPage />} />
                      <Route path="phone" element={<PhoneNumberBypass />} />
                      <Route path="phone/:otp" element={<PhoneNumberBypass />} />
                    </Route>
                    <Route path="*" element={<NotFound/>}/>
                </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
    )
}

export default Router
